import Component from 'flarum/common/Component';
import type Mithril from 'mithril';
import type { SkyspirePlayer } from '../services/skyspireApi';
import { skyspireTemplates } from '../data/skyspireTemplates';
import type { SkyspireTemplate, SkyspireTemplateCode } from '../data/skyspireTemplates';

interface SkyspireStarMapAttrs extends Mithril.Attributes {
  assetRoot: string;
  templateCode: SkyspireTemplateCode;
  players: SkyspirePlayer[];
  selectedSlot: number | null;
  onSelect: (slot: number) => void;
}

export default class SkyspireStarMap extends Component<SkyspireStarMapAttrs> {
  private edgePoints(template: SkyspireTemplate, from: number, to: number, index: number) {
    return [template.points[from], ...(template.linePoints[index] || []), template.points[to]];
  }

  private renderLines(template: SkyspireTemplate, device: 'pc' | 'phone') {
    return (
      <svg className={`SkyspireStarMap-lines SkyspireStarMap-lines--${device}`} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {template.edges.map(([from, to], index) => {
          const points = this.edgePoints(template, from, to, index)
            .map(({ x, y }) => `${x},${y}`)
            .join(' ');

          return <polyline key={`${device}-edge-${index}`} points={points} fill="none" />;
        })}
      </svg>
    );
  }

  private renderLineStars(template: SkyspireTemplate, device: 'pc' | 'phone') {
    return (
      <div className={`SkyspireLineStars SkyspireLineStars--${device}`} aria-hidden="true">
        {template.linePoints.map((edgePoints, edgeIndex) =>
          edgePoints.map((point, pointIndex) => {
            const style = {
              left: `${point.x}%`,
              top: `${point.y}%`,
              animationDelay: `${-((edgeIndex + pointIndex) % 7) * 0.37}s`,
            } as Mithril.CSSProperties;

            return <span key={`${device}-line-star-${edgeIndex}-${pointIndex}`} className="SkyspireLineStar" style={style}></span>;
          })
        )}
      </div>
    );
  }

  view() {
    const template = skyspireTemplates[this.attrs.templateCode];

    return (
      <div className="SkyspireStarMap">
        {this.renderLines(template.pc, 'pc')}
        {this.renderLines(template.phone, 'phone')}
        {this.renderLineStars(template.pc, 'pc')}
        {this.renderLineStars(template.phone, 'phone')}
        {template.pc.points.map((point, slot) => {
          const phonePoint = template.phone.points[slot];
          const player = this.attrs.players[slot];
          const hasFirst = !!player && player.rid !== null;
          const selected = hasFirst && this.attrs.selectedSlot === slot;
          const image = selected ? 'tqqx_7_light.png' : `tqqx_${hasFirst ? '7' : '8'}.png`;
          const phoneImage = selected ? image : `tqqx_${hasFirst ? '7' : '8'}_ph.png`;
          const style = {
            '--skyspire-pc-x': `${point.x}%`,
            '--skyspire-pc-y': `${point.y}%`,
            '--skyspire-phone-x': `${phonePoint.x}%`,
            '--skyspire-phone-y': `${phonePoint.y}%`,
          } as Mithril.CSSProperties;

          return (
            <button
              type="button"
              key={`star-${slot}`}
              className={`SkyspireStar${hasFirst ? ' is-lit' : ' is-unlit'}${selected ? ' is-selected' : ''}`}
              style={style}
              disabled={!hasFirst}
              aria-pressed={selected ? 'true' : 'false'}
              aria-label={player ? `${player.server} ${player.name || ''}` : undefined}
              onclick={() => hasFirst && this.attrs.onSelect(slot)}
            >
              <picture>
                <source media="(max-width: 767px)" srcset={`${this.attrs.assetRoot}/${phoneImage}`} />
                <img className="SkyspireStar-image" src={`${this.attrs.assetRoot}/${image}`} alt="" aria-hidden="true" />
              </picture>
              {hasFirst && player && <span className="SkyspireStar-label">{player.server}</span>}
            </button>
          );
        })}
      </div>
    );
  }
}
